import React, { useState } from 'react';
import { Modal } from '../common/Modal';
import { Calendar, Building2 } from 'lucide-react';
import { Package } from '../../types/package';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (packageData: Package) => Promise<void>;
  packageToEdit: Package;
};

export const EditPackageModal = ({ isOpen, onClose, onSave, packageToEdit }: Props) => {
  const { translations } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [packageData, setPackageData] = useState<Package>(packageToEdit);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setPackageData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Validate form before submission
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    // Required fields
    if (!packageData.name) newErrors.name = 'Package name is required';
    if (!packageData.mainMakkahHotel) newErrors.mainMakkahHotel = 'Main Makkah hotel is required';
    if (!packageData.mainMakkahStartDate) newErrors.mainMakkahStartDate = 'Main Makkah start date is required';
    if (!packageData.mainMakkahEndDate) newErrors.mainMakkahEndDate = 'Main Makkah end date is required';
    if (!packageData.madinahHotel) newErrors.madinahHotel = 'Madinah hotel is required';
    if (!packageData.madinahStartDate) newErrors.madinahStartDate = 'Madinah start date is required';
    if (!packageData.madinahEndDate) newErrors.madinahEndDate = 'Madinah end date is required';
    if (!packageData.packageType) newErrors.packageType = 'Package type is required';
    if (!packageData.camp) newErrors.camp = 'Camp is required';
    
    // Validate dates
    const validateDate = (dateStr: string) => {
      const dateRegex = /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[012])\/(19|20)\d\d$/;
      return dateRegex.test(dateStr);
    };
    
    const dateFields = [
      'mainMakkahStartDate', 'mainMakkahEndDate',
      'shiftingMakkahStartDate', 'shiftingMakkahEndDate',
      'madinahStartDate', 'madinahEndDate'
    ];
    
    dateFields.forEach(field => {
      const value = packageData[field as keyof Package] as string;
      if (value && !validateDate(value)) {
        newErrors[field] = 'Date must be in format DD/MM/YYYY';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    try {
      await onSave(packageData);
      onClose();
    } catch (error) {
      console.error('Error saving package:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={translations?.packages?.editPackage || 'Edit Package'}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Package Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {translations?.packages?.form?.name || 'Package Name'}
            <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            value={packageData.name}
            onChange={handleChange}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
              errors.name ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
        </div>

        {/* Main Makkah Hotel Section */}
        <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="font-medium flex items-center gap-2 text-gray-700">
            <Building2 className="text-primary-500" size={18} />
            {translations?.packages?.form?.mainMakkah || 'Main Makkah Hotel'}
          </h3>
          
          <div>
            <label htmlFor="mainMakkahHotel" className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.packages?.form?.hotelName || 'Hotel Name'}
              <span className="text-red-500">*</span>
            </label>
            <input
              id="mainMakkahHotel"
              name="mainMakkahHotel"
              type="text"
              value={packageData.mainMakkahHotel}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                errors.mainMakkahHotel ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.mainMakkahHotel && <p className="mt-1 text-sm text-red-500">{errors.mainMakkahHotel}</p>}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="mainMakkahStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.startDate || 'Start Date (DD/MM/YYYY)'}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="mainMakkahStartDate"
                  name="mainMakkahStartDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.mainMakkahStartDate}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.mainMakkahStartDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.mainMakkahStartDate && <p className="mt-1 text-sm text-red-500">{errors.mainMakkahStartDate}</p>}
            </div>
            
            <div>
              <label htmlFor="mainMakkahEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.endDate || 'End Date (DD/MM/YYYY)'}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="mainMakkahEndDate"
                  name="mainMakkahEndDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.mainMakkahEndDate}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.mainMakkahEndDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.mainMakkahEndDate && <p className="mt-1 text-sm text-red-500">{errors.mainMakkahEndDate}</p>}
            </div>
          </div>
          
          {/* Contract ID and dates */}
          <div>
            <label htmlFor="mainMakkahContractId" className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.packages?.form?.contractId || 'Contract ID'}
            </label>
            <input
              id="mainMakkahContractId"
              name="mainMakkahContractId"
              type="text"
              value={packageData.mainMakkahContractId || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="mainMakkahContractStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.contractStartDate || 'Contract Start Date'}
              </label>
              <div className="relative">
                <input
                  id="mainMakkahContractStartDate"
                  name="mainMakkahContractStartDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.mainMakkahContractStartDate || ''}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.mainMakkahContractStartDate ? 'border-red-500' : ''
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.mainMakkahContractStartDate && (
                <p className="mt-1 text-sm text-red-500">{errors.mainMakkahContractStartDate}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="mainMakkahContractEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.contractEndDate || 'Contract End Date'}
              </label>
              <div className="relative">
                <input
                  id="mainMakkahContractEndDate"
                  name="mainMakkahContractEndDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.mainMakkahContractEndDate || ''}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.mainMakkahContractEndDate ? 'border-red-500' : ''
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.mainMakkahContractEndDate && (
                <p className="mt-1 text-sm text-red-500">{errors.mainMakkahContractEndDate}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Shifting Makkah Hotel Section */}
        <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="font-medium flex items-center gap-2 text-gray-700">
            <Building2 className="text-blue-500" size={18} />
            {translations?.packages?.form?.shiftingMakkah || 'Shifting Makkah Hotel'} <span className="text-xs text-gray-500">(optional)</span>
          </h3>
          
          <div>
            <label htmlFor="shiftingMakkahHotel" className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.packages?.form?.hotelName || 'Hotel Name'}
            </label>
            <input
              id="shiftingMakkahHotel"
              name="shiftingMakkahHotel"
              type="text"
              value={packageData.shiftingMakkahHotel || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="shiftingMakkahStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.startDate || 'Start Date (DD/MM/YYYY)'}
              </label>
              <div className="relative">
                <input
                  id="shiftingMakkahStartDate"
                  name="shiftingMakkahStartDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.shiftingMakkahStartDate || ''}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.shiftingMakkahStartDate ? 'border-red-500' : ''
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.shiftingMakkahStartDate && <p className="mt-1 text-sm text-red-500">{errors.shiftingMakkahStartDate}</p>}
            </div>
            
            <div>
              <label htmlFor="shiftingMakkahEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.endDate || 'End Date (DD/MM/YYYY)'}
              </label>
              <div className="relative">
                <input
                  id="shiftingMakkahEndDate"
                  name="shiftingMakkahEndDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.shiftingMakkahEndDate || ''}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.shiftingMakkahEndDate ? 'border-red-500' : ''
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.shiftingMakkahEndDate && <p className="mt-1 text-sm text-red-500">{errors.shiftingMakkahEndDate}</p>}
            </div>
          </div>
          
          {/* Contract ID and dates */}
          <div>
            <label htmlFor="shiftingMakkahContractId" className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.packages?.form?.contractId || 'Contract ID'}
            </label>
            <input
              id="shiftingMakkahContractId"
              name="shiftingMakkahContractId"
              type="text"
              value={packageData.shiftingMakkahContractId || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="shiftingMakkahContractStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.contractStartDate || 'Contract Start Date'}
              </label>
              <div className="relative">
                <input
                  id="shiftingMakkahContractStartDate"
                  name="shiftingMakkahContractStartDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.shiftingMakkahContractStartDate || ''}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.shiftingMakkahContractStartDate ? 'border-red-500' : ''
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.shiftingMakkahContractStartDate && (
                <p className="mt-1 text-sm text-red-500">{errors.shiftingMakkahContractStartDate}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="shiftingMakkahContractEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.contractEndDate || 'Contract End Date'}
              </label>
              <div className="relative">
                <input
                  id="shiftingMakkahContractEndDate"
                  name="shiftingMakkahContractEndDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.shiftingMakkahContractEndDate || ''}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.shiftingMakkahContractEndDate ? 'border-red-500' : ''
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.shiftingMakkahContractEndDate && (
                <p className="mt-1 text-sm text-red-500">{errors.shiftingMakkahContractEndDate}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Madinah Hotel Section */}
        <div className="space-y-4 border border-gray-200 rounded-lg p-4 bg-gray-50">
          <h3 className="font-medium flex items-center gap-2 text-gray-700">
            <Building2 className="text-green-500" size={18} />
            {translations?.packages?.form?.madinah || 'Madinah Hotel'}
          </h3>
          
          <div>
            <label htmlFor="madinahHotel" className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.packages?.form?.hotelName || 'Hotel Name'}
              <span className="text-red-500">*</span>
            </label>
            <input
              id="madinahHotel"
              name="madinahHotel"
              type="text"
              value={packageData.madinahHotel}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                errors.madinahHotel ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.madinahHotel && <p className="mt-1 text-sm text-red-500">{errors.madinahHotel}</p>}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="madinahStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.startDate || 'Start Date (DD/MM/YYYY)'}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="madinahStartDate"
                  name="madinahStartDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.madinahStartDate}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.madinahStartDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.madinahStartDate && <p className="mt-1 text-sm text-red-500">{errors.madinahStartDate}</p>}
            </div>
            
            <div>
              <label htmlFor="madinahEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.endDate || 'End Date (DD/MM/YYYY)'}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  id="madinahEndDate"
                  name="madinahEndDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.madinahEndDate}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.madinahEndDate ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.madinahEndDate && <p className="mt-1 text-sm text-red-500">{errors.madinahEndDate}</p>}
            </div>
          </div>
          
          {/* Contract ID and dates */}
          <div>
            <label htmlFor="madinahContractId" className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.packages?.form?.contractId || 'Contract ID'}
            </label>
            <input
              id="madinahContractId"
              name="madinahContractId"
              type="text"
              value={packageData.madinahContractId || ''}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="madinahContractStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.contractStartDate || 'Contract Start Date'}
              </label>
              <div className="relative">
                <input
                  id="madinahContractStartDate"
                  name="madinahContractStartDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.madinahContractStartDate || ''}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.madinahContractStartDate ? 'border-red-500' : ''
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.madinahContractStartDate && (
                <p className="mt-1 text-sm text-red-500">{errors.madinahContractStartDate}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="madinahContractEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                {translations?.packages?.form?.contractEndDate || 'Contract End Date'}
              </label>
              <div className="relative">
                <input
                  id="madinahContractEndDate"
                  name="madinahContractEndDate"
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={packageData.madinahContractEndDate || ''}
                  onChange={handleChange}
                  className={`w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 ${
                    errors.madinahContractEndDate ? 'border-red-500' : ''
                  }`}
                />
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              </div>
              {errors.madinahContractEndDate && (
                <p className="mt-1 text-sm text-red-500">{errors.madinahContractEndDate}</p>
              )}
            </div>
          </div>
        </div>
        
        {/* Package Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="packageType" className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.packages?.form?.packageType || 'Package Type'}
              <span className="text-red-500">*</span>
            </label>
            <select
              id="packageType"
              name="packageType"
              value={packageData.packageType}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                errors.packageType ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Package Type</option>
              <option value="luxuary">Luxury</option>
              <option value="premium">Premium</option>
              <option value="standard">Standard</option>
              <option value="luxuary shifting">Luxury Shifting</option>
              <option value="premium shifting">Premium Shifting</option>
              <option value="standard shifting">Standard Shifting</option>
            </select>
            {errors.packageType && <p className="mt-1 text-sm text-red-500">{errors.packageType}</p>}
          </div>
          
          <div>
            <label htmlFor="camp" className="block text-sm font-medium text-gray-700 mb-1">
              {translations?.packages?.form?.camp || 'Camp'}
              <span className="text-red-500">*</span>
            </label>
            <select
              id="camp"
              name="camp"
              value={packageData.camp}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
                errors.camp ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Camp</option>
              <option value="Majar al kabas">Majar al kabas</option>
              <option value="al measuem">al measuem</option>
            </select>
            {errors.camp && <p className="mt-1 text-sm text-red-500">{errors.camp}</p>}
          </div>
        </div>
        
        {/* Buttons */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            {translations?.common?.cancel || 'Cancel'}
          </button>
          
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={isLoading}
            className={`px-6 py-2 rounded-lg text-white transition-colors ${
              isLoading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-primary-500 hover:bg-primary-600'
            }`}
          >
            {isLoading
              ? translations?.common?.saving || 'Saving...'
              : translations?.common?.saveChanges || 'Save Changes'}
          </motion.button>
        </div>
      </form>
    </Modal>
  );
};